import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Dialog from "@mui/material/Dialog";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
    ArrowLeft,
    Download,
} from "lucide-react";

import { baseUrl } from "../../constant/constant";
import { downloadTicketPdf } from "../../utils/downloadTicketPdf";
import {
    showPurchasedTicket,
    setPurchasedTickets,
} from "../../stores/slices/ticketSlice";
import { showError } from "../../stores/slices/commonSlice";
import { scheduleDocumentScrollLockRelease } from "../../utils/scrollLock";
import { MobileTicketStub } from "../../pages/tickets/ticketCards";

const TICKET_YELLOW = "#ffea00";
const CAROUSEL_GAP = 16;

/* =========================================================================
 *  Global post-purchase ticket viewer — horizontal swipe carousel.
 *
 *  Listens to `state.tickets.show_purchased_ticket` and renders the
 *  enriched rows from `state.tickets.purchased_tickets` (populated by
 *  the PaymentController success response and by the My Tickets /
 *  Lookup pages when the user opens an order).
 *
 *  Each stub is its own card in a snap-x scroll-mandatory horizontal
 *  scroller so the user swipes through their attendees. A page-dot
 *  row + "Ticket N of M" footer mirror the active index, which is
 *  derived from the scroller's onScroll handler.
 *
 *  The whole modal is no-padding edge-to-edge on mobile (Dialog
 *  fullScreen below sm) and a centered card on desktop. Either way
 *  the yellow stub stays the visual anchor.
 * ========================================================================= */
export default function PurchasedTicketModal() {
    const dispatch = useDispatch();
    const open = useSelector((s) => s.tickets.show_purchased_ticket);
    const tickets = useSelector((s) => s.tickets.purchased_tickets) || [];
    const [index, setIndex] = useState(0);
    const [pulseDownload, setPulseDownload] = useState(false);
    const scrollerRef = useRef(null);
    const isMobileViewport = useMediaQuery("(max-width:767px)");

    useEffect(() => {
        if (!open) return;
        setIndex(0);
        setPulseDownload(true);
        const t = setTimeout(() => setPulseDownload(false), 3000);
        requestAnimationFrame(() => {
            const el = scrollerRef.current;
            if (el) el.scrollLeft = 0;
        });
        return () => clearTimeout(t);
    }, [open]);

    // After an async purchase the QR image (barcode) is generated a few
    // seconds later by GenerateTicketArtifactsJob, so the initial snapshot can
    // arrive with empty barcodes and the card shows "Generating QR…" forever.
    // While the modal is open and any stub still lacks a barcode, poll the
    // public lookup (which signs barcodes) and refresh once they're ready.
    useEffect(() => {
        if (!open || !tickets.length) return;

        const hasBarcode = (s) => typeof s?.barcode === "string" && s.barcode.trim() !== "";
        if (tickets.every(hasBarcode)) return; // all present — nothing to poll

        const cid = tickets.find((s) => s?.confirmation_id)?.confirmation_id;
        if (!cid) return;

        let cancelled = false;
        let timer;
        let attempts = 0;

        const poll = async () => {
            attempts += 1;
            try {
                const res = await fetch(`${baseUrl}/ticket-lookup/${encodeURIComponent(cid)}`);
                const json = await res.json();
                const stubs = json?.data?.stubs;
                if (!cancelled && Array.isArray(stubs) && stubs.some(hasBarcode)) {
                    // Got the signed QR URLs — update the modal and stop (this
                    // effect re-runs and exits early once all are present).
                    // Same normalisation the Lookup page does: /ticket-lookup
                    // returns the street address as `address`, and the stub
                    // reads `venueaddress`. Without this the location block
                    // would blank out the moment the QR poll refreshed.
                    const raw = json?.data?.event;
                    const event = raw
                        ? {
                              ...raw,
                              venueaddress: raw.venueaddress || raw.address || "",
                          }
                        : raw;
                    dispatch(
                        setPurchasedTickets(
                            stubs.map((stub) => ({
                                ...stub,
                                event,
                                eventname:
                                    json?.data?.eventname ||
                                    event?.title ||
                                    event?.name ||
                                    "",
                            }))
                        )
                    );
                    return;
                }
            } catch (_) {
                // transient error — keep retrying
            }
            if (!cancelled && attempts < 15) {
                timer = setTimeout(poll, 4000); // ~1 min of polling
            }
        };

        timer = setTimeout(poll, 4000);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [open, tickets, dispatch]);

    const close = () => {
        dispatch(showPurchasedTicket(false));
        dispatch(setPurchasedTickets([]));
    };

    if (!tickets.length) return null;

    const eventTitle =
        tickets[0]?.eventname ||
        tickets[0]?.event?.title ||
        tickets[0]?.event?.name ||
        "My Ticket";

    const activeTicket = tickets[index] || tickets[0];

    const handleDownloadTicket = async () => {
        if (!activeTicket?.id && !activeTicket?.confirmation_id) return;
        try {
            // This used to branch on "is there a token?" and stop there, which
            // 404'd for a ticket bought WITHOUT signing in: such an order has
            // id_user = 0 and belongs to no account, so the authenticated
            // endpoint can never match it — and the post-purchase modal is
            // exactly where a guest with a stale token from an earlier session
            // hits that. downloadTicketPdf falls back to the confirmation-id
            // route, which is the correct one for a guest order.
            await downloadTicketPdf(activeTicket);
        } catch (err) {
            dispatch(showError(err.message || "Download failed"));
        }
    };

    const onScroll = (e) => {
        const el = e.currentTarget;
        const first = el.children[0];
        if (!first) return;
        const slideWidth = first.offsetWidth + CAROUSEL_GAP;
        const next = Math.round(el.scrollLeft / slideWidth);
        if (next !== index && next >= 0 && next < tickets.length) {
            setIndex(next);
        }
    };

    const isMobileTicketView = Boolean(open && isMobileViewport);

    return (
        <Dialog
            open={!!open}
            onClose={close}
            TransitionProps={{ onExited: scheduleDocumentScrollLockRelease }}
            maxWidth={isMobileTicketView ? false : "xs"}
            fullWidth={!isMobileTicketView}
            fullScreen={isMobileTicketView}
            className="purchased-ticket-dialog"
            BackdropProps={{
                sx: isMobileTicketView
                    ? { backgroundColor: TICKET_YELLOW }
                    : undefined,
            }}
            sx={
                isMobileTicketView
                    ? {
                          "&.purchased-ticket-dialog .MuiDialog-container": {
                              padding: "0 !important",
                              alignItems: "stretch",
                          },
                          "&.purchased-ticket-dialog .MuiDialog-paper": {
                              margin: "0 !important",
                              width: "100% !important",
                              maxWidth: "100% !important",
                              height: "100% !important",
                              maxHeight: "100% !important",
                              borderRadius: "0 !important",
                          },
                      }
                    : undefined
            }
            PaperProps={{
                sx: open
                    ? {
                          overflow: "visible",
                          backgroundColor: "transparent !important",
                          boxShadow: "none !important",
                          ...(isMobileTicketView
                              ? {
                                    margin: "0 !important",
                                    maxWidth: "100% !important",
                                    width: "100% !important",
                                    height: "100% !important",
                                    maxHeight: "100% !important",
                                    borderRadius: "0 !important",
                                }
                              : {
                                    borderRadius: "2rem !important",
                                    margin: 2,
                                    maxHeight: "calc(100vh - 32px)",
                                }),
                      }
                    : {},
            }}
        >
            <div
                className={`bg-[#ffea00] flex flex-col overflow-hidden ${
                    isMobileTicketView
                        ? "h-full min-h-[100dvh] w-full min-w-full rounded-none shadow-none"
                        : "w-full max-h-[min(90vh,800px)] shadow-2xl rounded-[2rem]"
                }`}
            >
                {/* ----- Fixed Header ----- */}
                <div className="flex-shrink-0 flex items-center justify-between mt-3 px-4 pb-2">
                    <button
                        type="button"
                        onClick={close}
                        aria-label="Close"
                        className="w-10 h-10 rounded-full bg-black/10 border-none hover:bg-black/15 flex items-center justify-center text-black flex-shrink-0"
                    >
                        <ArrowLeft size={20} strokeWidth={2.5} />
                    </button>

                    <div className="flex-1 min-w-0 text-center px-2">
                        <h2 className="text-lg font-black text-black leading-tight truncate">
                            {eventTitle}
                        </h2>
                    </div>

                    <button
                        type="button"
                        onClick={handleDownloadTicket}
                        aria-label="Download ticket"
                        className={`w-10 h-10 rounded-full bg-black/10 hover:bg-black/15 flex items-center border-none justify-center text-black flex-shrink-0 ${
                            pulseDownload ? "animate-pulse-infinite" : ""
                        }`}
                    >
                        <Download size={20} strokeWidth={2.5} />
                    </button>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain scrollbar-hide">
                    <div
                        ref={scrollerRef}
                        onScroll={onScroll}
                        className={`flex w-full overflow-x-auto snap-x snap-mandatory px-[7.5vw] py-2 items-center h-full [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] ${
                            tickets.length === 1 ? "justify-center" : ""
                        }`}
                    >
                        {tickets.map((stub, i) => (
                            <StubCard
                                key={stub.id || stub.confirmation_id || i}
                                stub={stub}
                            />
                        ))}
                    </div>
                </div>

                <div className="flex-shrink-0 pb-6 pt-2 text-center flex flex-col items-center w-full">
                    <p className="font-black text-gray-800 mb-2 text-sm">
                        Ticket {index + 1} of {tickets.length}
                    </p>
                    {tickets.length > 1 && (
                        <div className="flex justify-center space-x-2">
                            {tickets.map((_, i) => (
                                <div
                                    key={i}
                                    className={`w-2 h-2 rounded-full transition-colors ${
                                        i === index
                                            ? "bg-black shadow-sm"
                                            : "bg-black/10"
                                    }`}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </Dialog>
    );
}

/* ============================================================================
 *  StubCard — single ticket stub in the carousel.
 * ============================================================================ */
function StubCard({ stub }) {
    const fullName = [stub?.firstname, stub?.lastname]
        .filter(Boolean)
        .join(" ")
        .trim();

    const priceLabel = (() => {
        const n = parseFloat(stub?.price_paid);
        if (Number.isFinite(n)) {
            return n.toLocaleString("en-US", { maximumFractionDigits: 0 });
        }
        return stub?.price_paid ?? "";
    })();

    const currency = stub?.currency || stub?.event?.currency || "";

    const formatActivation = (iso) => {
        if (!iso) return "";
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return "";
        const datePart = d.toLocaleDateString("en-US", {
            weekday: "short",
            month: "short",
            day: "numeric",
            year: "numeric",
        });
        const timePart = d.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        });
        return `${datePart} — ${timePart}`;
    };

    const barcodeSrc =
        typeof stub?.barcode === "string" ? stub.barcode.trim() : "";

    const ticketTypeLabel = priceLabel
        ? `${stub.ticket_type} — ${currency ? `${currency} ` : ""}$${priceLabel}`
        : stub.ticket_type;

    // Venue name over street address, matching the Location block on My
    // Tickets' inline view. `address` is accepted alongside `venueaddress`
    // because /ticket-lookup names the field differently from /my-tickets.
    const venueAddress = (
        stub?.event?.venueaddress ||
        stub?.event?.address ||
        ""
    ).trim();
    const venueName = (
        stub?.event?.venue ||
        venueAddress.split(",")[0] ||
        ""
    ).trim();

    return (
        <MobileTicketStub
            confirmationId={stub.confirmation_id}
            attendeeName={fullName}
            ticketType={ticketTypeLabel}
            dateTimeLabel={formatActivation(stub.activation_date)}
            venueLabel={venueName}
            venueAddressLabel={
                venueAddress && venueAddress !== venueName ? venueAddress : ""
            }
            renderQr={() =>
                barcodeSrc ? (
                    <img
                        src={barcodeSrc}
                        alt="Ticket QR"
                        className="w-full h-full object-contain mix-blend-multiply pointer-events-none opacity-95"
                    />
                ) : (
                    <div className="w-full h-full bg-gray-100 rounded-xl flex items-center justify-center text-gray-400 text-sm font-medium text-center px-4">
                        Generating QR…
                    </div>
                )
            }
        />
    );
}
